/**
 * components/forms/AssignFormModal.tsx
 *
 * Created Apr 2026 (multi-user assignment):
 * Admin modal to assign/unassign a form template to multiple users.
 * Shows currently assigned users with revoke action.
 * Searchable user list to add new assignments.
 * On assign, creates an in-app notification for the target user.
 * Updated Apr 2026: notification insert now captures and propagates errors.
 * Updated Apr 2026: onClose accepts optional success flag; passes true when any
 *   assignment or revocation occurred so callers can refetch only when needed.
 */

import { useState, useEffect, useCallback } from 'react';
import { Search, X, UserPlus, Users, Check } from 'lucide-react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { captureError } from '../../utils/errorReporting';
import {
  useUsersList,
  getUserDisplayName,
  getUserInitials,
  getColorFromEmail,
  type UserListItem,
} from '../../hooks/useUsersList';

interface Assignment {
  id: string;
  user_id: string;
  is_active: boolean;
}

interface AssignFormModalProps {
  isOpen: boolean;
  onClose: (success?: boolean) => void;
  templateId: string;
  templateName: string;
}

export default function AssignFormModal({
  isOpen,
  onClose,
  templateId,
  templateName,
}: AssignFormModalProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { users, loading: usersLoading } = useUsersList();

  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [search, setSearch] = useState('');
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchAssignments = useCallback(async () => {
    if (!templateId) return;
    setLoadingAssignments(true);
    try {
      const { data, error } = await supabase
        .from('form_template_assignments')
        .select('id, user_id, is_active')
        .eq('template_id', templateId);
      if (error) throw error;
      setAssignments(data || []);
    } catch (err) {
      captureError(err, { component: 'AssignFormModal', action: 'fetchAssignments' });
      showToast('Erreur lors du chargement des attributions', 'error');
    } finally {
      setLoadingAssignments(false);
    }
  }, [templateId, showToast]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setHasChanges(false);
      fetchAssignments();
    }
  }, [isOpen, fetchAssignments]);

  const assignedUserIds = new Set(
    assignments.filter((a) => a.is_active).map((a) => a.user_id)
  );

  const handleAssign = async (targetUser: UserListItem) => {
    if (!user) return;
    setProcessingUserId(targetUser.id);
    try {
      const existing = assignments.find((a) => a.user_id === targetUser.id);

      if (existing) {
        const { error } = await supabase
          .from('form_template_assignments')
          .update({ is_active: true })
          .eq('id', existing.id);
        if (error) throw error;
        setAssignments((prev) =>
          prev.map((a) => (a.id === existing.id ? { ...a, is_active: true } : a))
        );
      } else {
        const { data, error } = await supabase
          .from('form_template_assignments')
          .insert({ template_id: templateId, user_id: targetUser.id, assigned_by: user.id })
          .select('id, user_id, is_active')
          .single();
        if (error) throw error;
        setAssignments((prev) => [...prev, data]);
      }

      const { error: notifError } = await supabase.from('notifications').insert({
        user_id: targetUser.id,
        type: 'form_assigned',
        title: 'Nouveau formulaire disponible',
        message: `Le formulaire "${templateName}" a été attribué à votre espace. Vous pouvez maintenant l'envoyer à vos clients.`,
        metadata: { template_id: templateId, template_name: templateName },
      });
      if (notifError) throw new Error(notifError.message);

      showToast(`Formulaire attribué à ${getUserDisplayName(targetUser)}`, 'success');
      setHasChanges(true);
    } catch (err) {
      captureError(err, { component: 'AssignFormModal', action: 'handleAssign' });
      showToast("Erreur lors de l'attribution", 'error');
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleUnassign = async (targetUser: UserListItem) => {
    setProcessingUserId(targetUser.id);
    const assignment = assignments.find((a) => a.user_id === targetUser.id);
    if (!assignment) { setProcessingUserId(null); return; }
    try {
      const { error } = await supabase
        .from('form_template_assignments')
        .update({ is_active: false })
        .eq('id', assignment.id);
      if (error) throw error;
      setAssignments((prev) =>
        prev.map((a) => (a.id === assignment.id ? { ...a, is_active: false } : a))
      );
      showToast(`Attribution retirée pour ${getUserDisplayName(targetUser)}`, 'success');
      setHasChanges(true);
    } catch (err) {
      captureError(err, { component: 'AssignFormModal', action: 'handleUnassign' });
      showToast("Erreur lors de la révocation", 'error');
    } finally {
      setProcessingUserId(null);
    }
  };

  const assignedUsers = users.filter((u) => assignedUserIds.has(u.id));
  const filteredUsers = search.trim()
    ? users.filter((u) => {
        const q = search.toLowerCase();
        return (
          getUserDisplayName(u).toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
        );
      })
    : users;
  const unassignedFiltered = filteredUsers.filter((u) => !assignedUserIds.has(u.id));

  const isLoading = loadingAssignments || usersLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onClose(hasChanges)}
      title={`Attribuer - ${templateName}`}
      size="md"
    >
      <div className="space-y-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="sm" />
          </div>
        ) : (
          <>
            {assignedUsers.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">
                  Utilisateurs assignés ({assignedUsers.length})
                </p>
                <div className="space-y-2">
                  {assignedUsers.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center gap-3 px-3 py-2.5 bg-primary-50/60 border border-primary-100 rounded-xl"
                    >
                      <div className={`w-8 h-8 rounded-full ${getColorFromEmail(u.email)} flex items-center justify-center flex-shrink-0`}>
                        <span className="text-xs font-bold text-white leading-none">
                          {getUserInitials(u)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 truncate">
                          {getUserDisplayName(u)}
                        </p>
                        <p className="text-xs text-neutral-500 truncate">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="flex items-center gap-1 text-xs text-primary-600 font-medium">
                          <Check className="w-3.5 h-3.5" />
                          Assigné
                        </span>
                        <button
                          onClick={() => handleUnassign(u)}
                          disabled={processingUserId === u.id}
                          className="p-1 rounded-lg hover:bg-red-50 text-neutral-400 hover:text-red-500 transition-colors disabled:opacity-50"
                          title="Retirer l'attribution"
                        >
                          {processingUserId === u.id ? (
                            <Spinner size="sm" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">
                Ajouter des utilisateurs
              </p>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom ou e-mail..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-white"
                />
              </div>

              <div className="max-h-60 overflow-y-auto space-y-1">
                {unassignedFiltered.length === 0 ? (
                  <div className="py-8 text-center">
                    <Users className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                    <p className="text-sm text-neutral-500">
                      {search
                        ? 'Aucun utilisateur trouvé'
                        : 'Tous les utilisateurs sont déjà assignés'}
                    </p>
                  </div>
                ) : (
                  unassignedFiltered.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neutral-50 transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-full ${getColorFromEmail(u.email)} flex items-center justify-center flex-shrink-0`}>
                        <span className="text-xs font-bold text-white leading-none">
                          {getUserInitials(u)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 truncate">
                          {getUserDisplayName(u)}
                        </p>
                        <p className="text-xs text-neutral-500 truncate">{u.email}</p>
                      </div>
                      <Button
                        variant="secondary"
                        onClick={() => handleAssign(u)}
                        disabled={processingUserId === u.id}
                        className="flex-shrink-0"
                      >
                        {processingUserId === u.id ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                            Attribuer
                          </>
                        )}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
